import { useCallback, useEffect, useState } from 'react';
import { __ } from 'i18n';
import { checkForNewVersion, hardReload } from 'utils/appUpdate';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
// Piso entre duas checagens. Sem ele, cada troca de aba dispara um fetch — e o
// atendente alterna de aba dezenas de vezes por hora.
const MIN_INTERVAL_BETWEEN_CHECKS_MS = 60 * 1000;

/**
 * Aviso discreto de deploy novo.
 *
 * Antes deste componente, o usuário só descobria que havia versão nova quando
 * esbarrava num chunk que já não existia — ou seja, quando o app já tinha
 * quebrado. Aqui a detecção é proativa: comparamos o `buildId` deste bundle com
 * o publicado em `/version.json` e oferecemos o hard reload antes do erro.
 *
 * Reaproveita as chaves de tradução de `errorBoundary` (mesmo texto, mesmo
 * contexto) para não depender de locales recém-publicados chegarem ao cliente.
 */
const UpdateAvailableBanner = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [reloading, setReloading] = useState(false);

    useEffect(() => {
        // Sem guarda de ambiente de propósito: em dev o `/version.json` não
        // existe e o dev server devolve o index.html, caso em que
        // `checkForNewVersion` já retorna false. Assim o comportamento não fica
        // amarrado ao mode com que o Cloudflare Pages roda o build.
        let cancelled = false;
        let lastCheckAt = 0;

        const check = async () => {
            if (cancelled || document.visibilityState !== 'visible') return;

            const now = Date.now();
            if (now - lastCheckAt < MIN_INTERVAL_BETWEEN_CHECKS_MS) return;
            lastCheckAt = now;

            if (await checkForNewVersion()) {
                if (!cancelled) setUpdateAvailable(true);
            }
        };

        const interval = window.setInterval(check, POLL_INTERVAL_MS);
        // Voltar para a aba é o momento mais provável de haver deploy novo
        // desde a última checagem.
        document.addEventListener('visibilitychange', check);
        void check();

        return () => {
            cancelled = true;
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', check);
        };
    }, []);

    const handleReload = useCallback(() => {
        setReloading(true);
        void hardReload();
    }, []);

    if (!updateAvailable) return null;

    return (
        <div
            role="status"
            style={{
                position: 'fixed',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '12px 16px',
                borderRadius: 8,
                background: 'rgba(32, 32, 36, 0.96)',
                color: '#fff',
                fontSize: 14,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.24)'
            }}
        >
            <span>{__('errorBoundary.newVersionTitle')}</span>
            <button
                type="button"
                onClick={handleReload}
                disabled={reloading}
                style={{
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    background: '#fff',
                    color: '#202024',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: reloading ? 'default' : 'pointer',
                    opacity: reloading ? 0.6 : 1
                }}
            >
                {__('errorBoundary.newVersionReload')}
            </button>
        </div>
    );
};

export default UpdateAvailableBanner;
